import { getDataSourceProvider } from "@esgt/helpers"
import type { DataSourceConfig } from "@esgt/types"
import { useEffect, useRef, useState } from "react"

interface UseDataSourceOptions {
  /** The data source config from the question input. If undefined, the hook is a no-op. */
  dataSource: DataSourceConfig | undefined
  /** Current value of the trigger input. */
  triggerValue: string
  /** Function to set a sibling form value (from react-hook-form's setValue). */
  setValue: (name: string, value: string) => void
  /** The number of inputs per repeatable group. */
  inputsPerGroup: number
  /** The index of the current group (0-based). */
  groupIndex: number
  /** Called after values are populated to persist the changes. */
  saveChanges: () => void
}

interface UseDataSourceResult {
  /** True while a lookup is in progress. */
  isLoading: boolean
}

/**
 * Hook that watches a trigger input's value, queries a third-party API
 * via the data source provider registry, and populates sibling form inputs
 * with the response.
 *
 * The lookup is debounced (default 800 ms) to avoid excessive API calls.
 *
 * Uses previous-value tracking so that the lookup only fires when the
 * trigger value genuinely changes from user interaction. On mount the
 * initial (possibly pre-filled) value is recorded but does not trigger
 * a fetch, which prevents overwriting manually-edited coordinates when
 * the page loads with a saved address.
 */
export function useDataSource({
  dataSource,
  triggerValue,
  setValue,
  inputsPerGroup,
  groupIndex,
  saveChanges,
}: UseDataSourceOptions): UseDataSourceResult {
  const [isLoading, setIsLoading] = useState(false)

  // Keep refs for callbacks so they're not in the effect deps
  // (they are new function references on every render).
  const saveChangesRef = useRef(saveChanges)
  saveChangesRef.current = saveChanges

  const setValueRef = useRef(setValue)
  setValueRef.current = setValue

  // Track the previous trigger value so we can distinguish between
  // initial hydration (where prevRef.current is undefined) and a
  // genuine user-driven change.
  const prevRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!dataSource) return

    const prev = prevRef.current
    prevRef.current = triggerValue

    // First time the effect runs (mount / hydration): record the value
    // but don't fetch. This avoids re-geocoding every pre-filled address
    // on page load and overwriting manually-edited coordinates.
    if (prev === undefined) return

    // Value hasn't actually changed (e.g. a re-render with same value).
    if (prev === triggerValue) return

    // Don't fire for very short values (e.g. partial typing).
    if (!triggerValue || triggerValue.trim().length < 3) return

    const provider = getDataSourceProvider(dataSource.provider)
    if (!provider) {
      console.warn(`[useDataSource] Unknown data source provider: "${dataSource.provider}"`)
      return
    }

    const debounceMs = dataSource.debounceMs ?? 800
    let cancelled = false
    setIsLoading(true)

    const timer = setTimeout(async () => {
      try {
        const result = await provider.fetch(triggerValue)
        if (cancelled || !result) {
          if (!cancelled) setIsLoading(false)
          return
        }

        for (const mapping of dataSource.mappings) {
          const targetFieldName = `input_${groupIndex * inputsPerGroup + mapping.targetInput}`
          const value = result[mapping.field]
          if (value !== undefined) {
            setValueRef.current(targetFieldName, String(value))
          }
        }

        // Persist all populated values in one go
        saveChangesRef.current()
      } catch (err) {
        console.warn(`[useDataSource] Lookup failed for provider "${dataSource.provider}":`, err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }, debounceMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
      setIsLoading(false)
    }
  }, [dataSource, triggerValue, inputsPerGroup, groupIndex])

  return { isLoading }
}
